import { createElement, useMemo, useRef, useState, forwardRef } from 'react';

import { Label } from '../../Label';
import { RadioButtonProps } from '.';
import { useDesignTokens, renderDesignTokens, DesignTokens } from '../../../theme';
import { Text } from '../../Text';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { setRef } from '../../../util/ref-util';
import { setAlpha } from '../../../design-tokens';
import cx from 'classnames';
import cn from './RadioButton.css.json';
import './RadioButton.css';

const useHtmlId = createUseHtmlId('RadioButton');
/**
 * @internal
 */
export const UnthemedRadioButton = forwardRef<HTMLDivElement, RadioButtonProps>((props, ref) => {
  const [inputId, labelId, descriptionId] = useHtmlId(3);

  const radioInput = useRef<HTMLInputElement>(null);
  const [isFocusVisible, setIsFocusVisible] = useState<boolean>(false);
  const handleFocus = () => {
    radioInput.current?.focus();
  };

  const handleInputFocus = () => {
    setIsFocusVisible(true);
    props.onFocus?.();
  };

  const handleInputBlur = () => {
    setIsFocusVisible(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // only spacebar
    if (event.key === ' ') {
      props.onPress?.();
    }
  };

  const rbRef = useRef<HTMLDivElement | null>(null);
  const dt = useDesignTokens();

  // line-height patch specific to radio button to help keep radio button and its label centered
  // NOTE: this will break if the unit is not px
  const labelFontSizeInPixels =
    parseFloat(dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m) * 16;
  const labelHeight = Math.round(
    labelFontSizeInPixels * (dt.enableNovaTokens ? dt.fontNova.lineHeight : dt.font.lineHeight)
  );

  // the 14 is the height of the checkbox in pixels. Only define the custom margin if a label is supplied.
  const marginTopCalc = props.label ? `${Math.floor((labelHeight - 14) / 2)}px` : undefined;

  return (
    <div className={cx(props.className)} style={props.style} data-testid={props.testId}>
      <div
        ref={(element) => {
          rbRef.current = element;
          setRef(ref, element);
        }}
        className={cx(
          cn.normal,
          props.selected && cn.selected,
          props.disabled && cn.disabled,
          props.readOnly && cn.readOnly,
          !props.label && cn.noLabel,
          isFocusVisible && cn.focusVisible
        )}
        tabIndex={!isFocusVisible && props.focusable ? 0 : -1}
        onFocus={handleFocus}
        onMouseDown={props.onPress}
        title={props.label ? undefined : props.tooltip}
      >
        <span
          className={cx(cn.inputContainer, props.className)}
          style={{ marginTop: marginTopCalc }}
        >
          <input
            id={inputId}
            ref={radioInput}
            className={cn.input}
            value={props.value}
            name={props.groupName}
            type="radio"
            style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0 }}
            tabIndex={isFocusVisible ? 0 : -1}
            onChange={props.onPress ?? (() => {})}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            role="radio"
            aria-disabled={props.disabled}
            aria-checked={props.selected}
            aria-labelledby={
              props.label ? `${labelId} ${props.ariaLabelledBy ?? ''}`.trim() : props.ariaLabelledBy
            }
            aria-describedby={descriptionId}
            checked={props.selected}
            onKeyDown={handleKeyDown}
          />
        </span>
        {props.label ? (
          <Label
            id={labelId}
            disabled={props.disabled}
            className={cn.label}
            wrapText={props.wrapLabel ?? true}
            tokens={() => ({ fg: dt.c.colorTextDefault })}
          >
            {props.label}
          </Label>
        ) : null}
      </div>
      {props.description ? (
        <div className={cn.description}>
          <Text
            id={descriptionId}
            disabled={props.disabled}
            className={cx(cn.descriptionText, props.disabled && cn.disabled)}
            wrapText={props.wrapDescription ?? true}
          >
            {props.description}
          </Text>
        </div>
      ) : null}
    </div>
  );
});

/**
 * @internal
 */
export const RadioButton = forwardRef<HTMLDivElement, RadioButtonProps>((props, ref) => {
  const dt = useDesignTokens();
  const tokens = useMemo(() => getRadioButtonDesignTokens(dt), [dt]);
  return <UnthemedRadioButton ref={ref} {...props} style={{ ...tokens, ...props.style }} />;
});

export const getRadioButtonDesignTokens = (dt: DesignTokens) => {
  const tokens = dt.stateSecondaryToggleNova;

  const readOnlyTokens = dt.stateSecondaryToggleNova.readOnly;
  /*
   * List sets these tokens at the root of the List for performance considerations.
   * Without knowledge of the state of each radio button in the list, it needs a separate token
   * for each state.
   */
  return {
    ...renderDesignTokens(
      {
        'spacing-s': dt.spacing.core.s,
        'spacing-xs': dt.spacing.core.xs,
        'spacing-xxs': dt.spacing.core.xxs,
        ...tokens.normal,
        'font-size-s': dt.fontNova.size.s,
        'description-fg': dt.c.colorTextLabel,
      },
      'radio'
    ),
    ...renderDesignTokens(tokens.selected, 'radio-selected'),
    ...renderDesignTokens(tokens.disabled, 'radio-disabled'),
    ...renderDesignTokens(tokens.disabledSelected, 'radio-disabled-selected'),
    ...renderDesignTokens(readOnlyTokens, 'radio-read-only'),
    ...(!dt.enableNovaTokens && getRadioButtonLegacyDesignTokens(dt)),
  };
};

export const getRadioButtonLegacyDesignTokens = (dt: DesignTokens) => {
  const tokens = dt.stateSecondaryToggle;
  let readOnlyTokens = { fg: dt.c.n_3, bg: dt.c.white, bd: dt.c.n2 };
  switch (dt.themeVariant) {
    case 'dark':
      readOnlyTokens = { fg: dt.c.white, bg: dt.c.p_3, bd: setAlpha(dt.c.white, 0.2) };
      break;
    case 'high-contrast':
      readOnlyTokens = { fg: dt.c.white, bg: dt.c.black, bd: dt.c.white };
      break;
  }
  return {
    ...renderDesignTokens(
      {
        ...tokens.normal,
        'font-size-s': dt.font.size.s,
        'description-fg': dt.c.colorTextLabel,
      },
      'radio'
    ),
    ...renderDesignTokens(tokens.selected, 'radio-selected'),
    ...renderDesignTokens(tokens.disabled, 'radio-disabled'),
    ...renderDesignTokens(tokens.disabledSelected, 'radio-disabled-selected'),
    ...renderDesignTokens(readOnlyTokens, 'radio-read-only'),
  };
};

/**
 * @deprecated use `getRadioButtonDesignTokens()` instead
 */
export const getRadioButtonThemeTokens = (dt: DesignTokens) => getRadioButtonDesignTokens(dt);
